import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { captureError } from '@shared/lib/observability';

interface MergeRow {
  kind: 'industry' | 'category' | 'subcategory' | string;
  id: string;
  name: string | null;
  slug: string | null;
  parent_id: string | null;
  parent_name: string | null;
  usage_count: number | null;
}

interface PeerRow { id: string; name: string | null }

function kindTable(kind: string) {
  if (kind === 'category') return 'categories';
  return 'subcategories';
}
function kindParentCol(kind: string): string | null {
  // 2-tier: categories are top-level (no parent); subcategories hang off a category.
  if (kind === 'subcategory') return 'category_id';
  return null;
}

export default function Taxonomy() {
  const { t } = useTranslation('admin');
  const invalidate = useInvalidateSupabase();
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [peers, setPeers] = useState<Record<string, PeerRow[]>>({});

  const { data, isLoading, error } = useSupabaseQuery<MergeRow[]>(
    ['admin-merge-queue'],
    async () => supabase
      .from('v_taxonomy_merge_queue')
      .select('kind, id, name, slug, parent_id, parent_name, usage_count')
      .order('usage_count', { ascending: false })
      .limit(500),
  );

  const rows = data ?? [];

  // Load peer candidates per distinct (kind, parent_id)
  useEffect(() => {
    if (!rows.length) return;
    let cancelled = false;
    (async () => {
      const needed = new Map<string, { kind: string; parent_id: string | null }>();
      rows.forEach((r) => {
        const k = `${r.kind}|${r.parent_id ?? 'root'}`;
        if (!needed.has(k)) needed.set(k, { kind: r.kind, parent_id: r.parent_id });
      });
      const out: Record<string, PeerRow[]> = {};
      for (const [k, { kind, parent_id }] of needed) {
        const tbl = kindTable(kind);
        const parentCol = kindParentCol(kind);
        const sb = supabase.from(tbl) as unknown as {
          select: (s: string) => {
            eq?: (c: string, v: string) => unknown;
            order: (c: string) => Promise<{ data: PeerRow[] | null }>;
          };
        };
        let q: any = sb.select('id, name');
        if (parentCol && parent_id) q = q.eq(parentCol, parent_id);
        const { data: d } = await q.order('name');
        out[k] = d ?? [];
      }
      if (!cancelled) setPeers(out);
    })();
    return () => { cancelled = true; };
  }, [rows]);

  const bucketStats = useMemo(() => {
    const out: Record<string, number> = { category: 0, subcategory: 0 };
    rows.forEach((r) => { out[r.kind] = (out[r.kind] ?? 0) + 1; });
    return out;
  }, [rows]);

  const merge = async (row: MergeRow) => {
    const into = picked[row.id];
    if (!into) { alert(t('tax.pickPeer')); return; }
    if (into === row.id) { alert(t('tax.cantMergeSelf')); return; }
    const target = (peers[`${row.kind}|${row.parent_id ?? 'root'}`] ?? []).find((p) => p.id === into);
    const ok = window.confirm(
      `${t('tax.confirmMerge')}\n\n "${row.name}"\n →  "${target?.name ?? into}"\n\n${t('tax.confirmWarn')}`,
    );
    if (!ok) return;
    setBusy((b) => ({ ...b, [row.id]: true }));
    const { error: e } = await supabase.rpc('merge_taxonomy_row', {
      p_kind: row.kind,
      p_from: row.id,
      p_into: into,
    });
    setBusy((b) => ({ ...b, [row.id]: false }));
    if (e) { captureError(e, { where: 'admin.taxonomy.merge', kind: row.kind, from: row.id, into }); alert(`Merge failed: ${e.message}`); return; }
    setPicked((p) => ({ ...p, [row.id]: '' }));
    invalidate(['admin-merge-queue']);
  };

  return (
    <SectionErrorBoundary name="admin-taxonomy">
      <h2 className="admin-page-title">{t('tax.title')}</h2>
      <p className="admin-page-sub">{t('tax.sub')}</p>

      <div className="admin-kpis">
        <div className="admin-kpi grad">
          <div className="lbl">{t('tax.toResolve')}</div>
          <div className="val">{rows.length}</div>
        </div>
        <div className="admin-kpi">
          <div className="lbl">{t('tax.categories')}</div>
          <div className="val">{bucketStats.category ?? 0}</div>
        </div>
        <div className="admin-kpi">
          <div className="lbl">{t('tax.subcategories')}</div>
          <div className="val">{bucketStats.subcategory ?? 0}</div>
        </div>
      </div>

      {isLoading && <Skeleton height={300} />}
      {error && <div style={{ color: 'var(--danger)' }}>{String((error as Error)?.message ?? error)}</div>}

      {!isLoading && !error && rows.length === 0 && (
        <div className="admin-empty">{t('tax.clean')}</div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('tax.colKind')}</th>
                <th>{t('tax.colParent')}</th>
                <th>{t('tax.colPolluted')}</th>
                <th style={{ textAlign: 'right' }}>{t('tax.colUsage')}</th>
                <th style={{ minWidth: 240 }}>{t('tax.colMergeInto')}</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const opts = (peers[`${r.kind}|${r.parent_id ?? 'root'}`] ?? []).filter(
                  (p) => p.id !== r.id,
                );
                return (
                  <tr key={r.id}>
                    <td><span className={`admin-pill kind-${r.kind}`}>{r.kind}</span></td>
                    <td className="dim">{r.parent_name ?? '—'}</td>
                    <td style={{ fontWeight: 'var(--fw-semibold)' }}>
                      {r.name}{' '}
                      <span className="dim" style={{ fontSize: 'var(--fs-caption)' }}>{r.slug ?? ''}</span>
                    </td>
                    <td className="num">{r.usage_count?.toLocaleString() ?? 0}</td>
                    <td>
                      <select
                        className="admin-inline-select"
                        value={picked[r.id] ?? ''}
                        onChange={(e) => setPicked((p) => ({ ...p, [r.id]: e.target.value }))}
                      >
                        <option value="">{t('tax.pickCleanPeer')}</option>
                        {opts.map((o) => (
                          <option key={o.id} value={o.id}>{o.name ?? o.id}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        className="admin-btn-merge"
                        disabled={!picked[r.id] || busy[r.id]}
                        onClick={() => merge(r)}
                      >
                        {busy[r.id] ? '…' : t('tax.merge')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionErrorBoundary>
  );
}
